import { useEffect, useState } from "react";
import {
  Save,
  Loader2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Monitor,
  LayoutGrid,
  Music,
  Gamepad2,
  Disc3,
  Eraser,
  Brain,
  Volume2,
} from "lucide-react";
import type { LogoPosition, PremioPublic, RaspaditaMecanica } from "@juegossc/shared";
import {
  superGetGlobalSettings,
  superUpdateGlobalSettings,
  superListSounds,
  superListCampanas,
  adminListProductos,
  adminListSoundStyles,
  type GlobalSettings,
  type SoundAsset,
  type AssetKind,
  type SoundStylePublic,
} from "../../lib/api";
import { SOUND_SLOTS, playEvent, type SoundSlot } from "../../lib/audio";
import { applyFonts, preloadAllFontOptions } from "../../lib/fonts";
import { useDialogs } from "../../hooks/useDialogs";
import { useAdminCtx } from "./AdminLayout";
import { Section, SettingCard } from "../../components/admin/forms/SettingCard";
import { SliderField } from "../../components/admin/forms/SliderField";
import { ColorField } from "../../components/admin/forms/ColorField";
import { NumberField } from "../../components/admin/forms/NumberField";
import { ToggleRow } from "../../components/admin/forms/ToggleRow";
import { HelpPopover } from "../../components/admin/forms/HelpPopover";
import { AssetUploader } from "../../components/admin/AssetUploader";
import { FontPicker } from "../../components/admin/FontPicker";
import { PantallaPreview } from "../../components/admin/previews/PantallaPreview";
import { GamePreviewFrame } from "../../components/admin/previews/GamePreviewFrame";
import { JuegoPreview } from "../../components/admin/previews/JuegoPreview";
import { SoundUploader } from "../../components/admin/sounds/SoundUploader";
import { SoundsByStyleAndEvent } from "../../components/admin/sounds/SoundsByStyleAndEvent";
import { SoundStylesCRUD } from "../../components/admin/sounds/SoundStylesCRUD";
import { SoundStyleCard } from "../../components/admin/sounds/SoundStyleCard";

// Placeholder dentro del marco portrait cuando el juego de la sub-tab está
// DESHABILITADO: si está OFF no hay pantalla real que mostrar, avisamos en vez
// de dejar el marco vacío. (Movido de Configuracion al migrar el config de
// juegos a global, migration 038.)
function PreviewDeshabilitado({ nombre }: { nombre: string }) {
  return (
    <div className="flex h-full w-full items-center justify-center p-6 text-center text-sm text-white/60">
      {nombre} está deshabilitada. Activala para ver la vista previa.
    </div>
  );
}

// Pestañas internas. Mantener orden = orden de aparición en la UI.
// Las keys también las usamos como IDs internos para tracking, no
// están expuestas en URL — la página queda en /admin/shopping.
// Migration 026: pestaña "Ruleta" se eliminó — sus settings visuales
// (colores, grosor, alineación) viven ahora en /admin/pantalla (Mi
// pantalla → Juegos → Ruleta) porque cada sucursal personaliza su
// ruleta sin afectar a las demás.
// UNIR_MARCA_PANTALLA (R4): el dueño pidió fusionar las viejas pestañas
// "Marca" (logo + tipografías) y "Pantalla principal" en un solo panel —
// todo lo del landing vive junto. Se suma "Pantalla de juego" (lo que ve el
// cliente al ENTRAR a un juego: fondo portrait + campaña en el header).
// MOVER_JUEGOS (migration 038): el config de juegos (ruleta/raspadita/memoria)
// dejó de ser por-sucursal y pasó a ser GLOBAL del shopping. Por eso la pestaña
// "Juegos" (que vivía en Mi pantalla › Juegos) se mudó acá: el super-admin edita
// todo el config de juegos en un solo lugar, cableado a global_settings.
type TabKey = "pantalla" | "distribucion" | "juegos" | "sonidos";
const TABS: { key: TabKey; label: string; Icon: typeof Monitor }[] = [
  { key: "pantalla", label: "Pantalla principal", Icon: Monitor },
  { key: "distribucion", label: "Distribución", Icon: LayoutGrid },
  { key: "juegos", label: "Juegos", Icon: Gamepad2 },
  { key: "sonidos", label: "Sonidos", Icon: Music },
];

// Sub-pestañas dentro de "Juegos" — cada juego en su propia vista para evitar el
// scroll largo cuando los 3 están habilitados. Misma estrategia de visibilidad
// CSS que las pestañas top-level: los inputs viven en el mismo <form>, se
// guardan todos juntos con el "Guardar cambios" del pie.
type JuegoTabKey = "ruleta" | "raspadita" | "memoria";
const JUEGO_TABS: { key: JuegoTabKey; label: string; Icon: typeof Disc3 }[] = [
  { key: "ruleta", label: "Ruleta", Icon: Disc3 },
  { key: "raspadita", label: "Raspadita", Icon: Eraser },
  { key: "memoria", label: "Memoria", Icon: Brain },
];

// Límites del logo y campaña — mantener en sync con migration 011 (CHECK).
const LOGO_MIN_PX = 60;
const LOGO_MAX_PX = 1200;
const CAMPANA_MIN_PX = 80;
const CAMPANA_MAX_PX = 800;
// Migration 034: rango del alto de la campaña dentro del juego (CHECK 40..400).
const CAMPANA_JUEGO_MIN_PX = 40;
const CAMPANA_JUEGO_MAX_PX = 400;
// Migration 035: rango del aire-arriba de la campaña dentro del juego
// (empuja la campaña hacia abajo en la pantalla del juego, 0..400).
const CAMPANA_JUEGO_TOP_MARGIN_MIN_PX = 0;
const CAMPANA_JUEGO_TOP_MARGIN_MAX_PX = 400;
// Migration 039: rangos del tamaño del cuerpo de los juegos sobre el lienzo
// 1080x1920 (mantener en sync con el CHECK de la migration).
const MEMORIA_CARTA_MIN_PX = 100;
const MEMORIA_CARTA_MAX_PX = 300;
const RASPADITA_PANEL_MIN_PX = 300;
const RASPADITA_PANEL_MAX_PX = 1000;
// Migration 044: tamaño de la rueda (px sobre el lienzo) + aire arriba del logo.
const RULETA_WHEEL_MIN_PX = 300;
const RULETA_WHEEL_MAX_PX = 960;
const LOGO_TOP_MARGIN_MIN_PX = 0;
const LOGO_TOP_MARGIN_MAX_PX = 600;
// Migration 026: constantes DIVISORIA_MIN/MAX migraron a Configuracion.tsx
// (cada sucursal personaliza su ruleta).

export default function GlobalSettingsPage() {
  const dialogs = useDialogs();
  const { setPageDirty, sucursalIdEfectiva } = useAdminCtx();
  // Aviso de cambios sin guardar al cambiar de sucursal (que remonta la
  // pagina). El onChange del form cubre los edits comunes (colores,
  // sliders, tipos de texto). Nota: cambios solo-por-boton (subir un
  // archivo, elegir fuente/alineacion) no marcan dirty acá — pero la
  // sucursal activa no afecta a Ajustes del shopping (es global), asi
  // que cambiar de sucursal en medio de editar esto es poco comun.
  useEffect(() => () => setPageDirty(false), [setPageDirty]);
  const [settings, setSettings] = useState<GlobalSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state (mirror de settings)
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [bgLandscapeUrl, setBgLandscapeUrl] = useState<string | null>(null);
  const [bgPortraitUrl, setBgPortraitUrl] = useState<string | null>(null);
  // SACAR_COLORES (R4): la card "Colores" (color_primary/color_accent) era un
  // control muerto — theme.ts setea --color-primary/--color-accent pero ningún
  // componente ni regla CSS las consume (solo --color-bg/--color-surface, que
  // son otras). "Si no hace nada, desaparecer": se quitó el estado, su
  // hidratación y los campos del PUT. Las columnas del backend quedan intactas.
  // Migration 029: soundEnabled + soundStyle migran a /admin/pantalla
  // (por sucursal). Estilos disponibles + CRUD viven en estado aparte.
  // Migration 011: controles manuales del logo + alto máx de la campaña.
  const [logoWidthPx, setLogoWidthPx] = useState(350);
  const [logoPosition, setLogoPosition] = useState<LogoPosition>("center");
  const [campanaMaxHeightPx, setCampanaMaxHeightPx] = useState(240);
  // Migration 034: alto de la campaña DENTRO del juego (default 80 = el
  // cap viejo). Separado del alto en la portada.
  const [campanaJuegoHeightPx, setCampanaJuegoHeightPx] = useState(80);
  // Migration 035: aire-arriba de la campaña dentro del juego (default 0).
  // Empuja la campaña hacia abajo en la pantalla del juego sin tocar su alto.
  const [campanaJuegoTopMarginPx, setCampanaJuegoTopMarginPx] = useState(0);
  // Migration 037: posición vertical del cuerpo del juego (0..100%, deslizador).
  const [juegoVerticalPosPct, setJuegoVerticalPosPct] = useState(50);
  // Migration 040: nitidez de los canvas de juego (cap de DPR, 1..3). Default 2.
  const [juegoCanvasDprMax, setJuegoCanvasDprMax] = useState(2);
  // Migration 013/033: íconos custom para los botones de juego (incluye Memoria).
  const [iconRuletaUrl, setIconRuletaUrl] = useState<string | null>(null);
  const [iconRaspaditaUrl, setIconRaspaditaUrl] = useState<string | null>(null);
  const [iconMemoriaUrl, setIconMemoriaUrl] = useState<string | null>(null);
  // Migration 027: tamaño en px de los botones de juego del menú.
  // Default 224 (lo que estaba hardcoded antes).
  const [landingIconSizePx, setLandingIconSizePx] = useState(224);
  // Migration 031: layout configurable del landing (campaña + íconos).
  const [campanaToIconosGapPx, setCampanaToIconosGapPx] = useState(64);
  // Migration 045: posición vertical fina del bloque campaña+íconos (0..100%).
  // Reemplaza el enum iconosVerticalAlign (top/center/bottom).
  const [landingVerticalPosPct, setLandingVerticalPosPct] = useState(50);
  const [iconosHorizontalAlign, setIconosHorizontalAlign] = useState<
    "left" | "center" | "right"
  >("center");
  const [campanaTopMarginPx, setCampanaTopMarginPx] = useState(0);
  // Migration 044: aire arriba del logo en la pantalla principal (lo baja desde
  // el borde superior). Default 0.
  const [logoTopMarginPx, setLogoTopMarginPx] = useState(0);
  // Migration 026: ruletaColorBorde/Centro/GrosorDivisoria/TextAlign
  // migraron a /admin/pantalla (por sucursal). No viven más acá.
  // Migration 018: tipografías configurables.
  const [fontHeading, setFontHeading] = useState("Nunito");
  const [fontBody, setFontBody] = useState("Nunito");
  const [fontDisplay, setFontDisplay] = useState("Nunito");
  // MOVER_MEMORIA: el estado memoriaImagenes (6 urls) se quitó — la card de
  // imágenes de Memoria salió de esta página (se gestiona en Mi pantalla ›
  // Juegos › Memoria). El backend preserva las urls guardadas porque el PUT
  // las omite (CASE WHEN flag=1, ver updateGlobalSettings).
  // Migration 017: lista de sounds cargados. Se refresca después de
  // cada upload/delete para reflejar el estado real.
  const [sounds, setSounds] = useState<SoundAsset[]>([]);
  // Migration 041: sonido del shopping (estilo + habilitado). GLOBAL — uno solo
  // para todas las sucursales (antes era por sucursal). Se guarda con el
  // "Guardar cambios" del pie, junto al resto del config global.
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [soundStyleSlug, setSoundStyleSlug] = useState("classic");
  // Lista de estilos disponibles para el selector (solo activos). Si el
  // super_admin marca un estilo como inactivo, deja de aparecer acá pero el
  // valor guardado se conserva en DB.
  const [soundStyles, setSoundStyles] = useState<SoundStylePublic[]>([]);

  // Pestaña activa (no en URL, solo state local).
  const [activeTab, setActiveTab] = useState<TabKey>("pantalla");
  // Sub-pestaña de "Distribución": pantalla principal (landing) vs dentro del
  // juego. Define también qué preview se ve a la derecha (landing vs ruleta).
  const [distribSub, setDistribSub] = useState<"principal" | "juego">(
    "principal",
  );
  // Sub-pestaña de "Juegos": Ruleta / Raspadita / Memoria (cada juego en su
  // propia vista). También define qué juego muestra el preview de la derecha.
  const [activeJuegoTab, setActiveJuegoTab] = useState<JuegoTabKey>("ruleta");

  // ── Form state — config de juegos (GLOBAL, migration 038). Movido de
  // Configuracion (Mi pantalla › Juegos), que lo guardaba por sucursal.
  // Ruleta
  const [ruletaHab, setRuletaHab] = useState(true);
  const [velocidadMin, setVelocidadMin] = useState(0.4);
  const [variacion, setVariacion] = useState(0.6);
  const [frenado, setFrenado] = useState(0.985);
  const [umbralCasi, setUmbralCasi] = useState(0.2);
  const [colorBorde, setColorBorde] = useState("#c98b1c");
  const [colorCentro, setColorCentro] = useState("#f7c948");
  const [grosorDivisoria, setGrosorDivisoria] = useState(3);
  const [textAlign, setTextAlign] = useState<"left" | "center" | "right">(
    "left",
  );
  // Migration 044: tamaño TARGET de la rueda (default 520 = el cap hardcodeado).
  const [ruletaWheelSizePx, setRuletaWheelSizePx] = useState(520);
  // Raspadita
  const [raspaditaHab, setRaspaditaHab] = useState(true);
  const [mecanica, setMecanica] = useState<RaspaditaMecanica>("panel");
  // Migration 039: tamaño del panel de raspadita sobre el lienzo (defaults = los
  // valores hardcoded previos).
  const [raspaditaPanelWPx, setRaspaditaPanelWPx] = useState(640);
  const [raspaditaPanelHPx, setRaspaditaPanelHPx] = useState(460);
  // raspadita_grid_dinamica se hardcodea a 'encontrar' al guardar (única sana
  // según sim-mecanicas; autocura cualquier valor legacy), igual que antes.
  // Memoria
  const [memoriaHab, setMemoriaHab] = useState(false);
  // Migration 039: lado de las cartas del tablero (default = hardcoded previo).
  const [memoriaCartaPx, setMemoriaCartaPx] = useState(200);
  const [segMemFacil, setSegMemFacil] = useState(3.0);
  const [segMemMedio, setSegMemMedio] = useState(2.0);
  const [segMemDificil, setSegMemDificil] = useState(1.0);
  const [memTFacil, setMemTFacil] = useState(30);
  const [memTMedio, setMemTMedio] = useState(20);
  const [memTDificil, setMemTDificil] = useState(15);
  // Imágenes globales del tablero de memoria (6 slots). Se persisten al instante
  // vía superUpdateGlobalSettings (no esperan al "Guardar cambios"), igual que
  // antes en Configuracion. Se hidratan en refetch().
  const [memoriaImagenes, setMemoriaImagenes] = useState<Array<string | null>>(
    [null, null, null, null, null, null],
  );

  // Campaña activa REAL (nombre + imagen) para que el preview muestre la
  // campaña tal cual se verá, no un placeholder. Se trae de superListCampanas
  // (la que tiene activa===1). La imagen no está en el form (vive en Campañas).
  const [campanaActiva, setCampanaActiva] = useState<
    { nombre_display: string; imagen_url: string } | null
  >(null);

  // Cantidad de premios activos en la ruleta de la sucursal seleccionada →
  // el ícono por defecto de Ruleta dibuja esa cantidad de gajos (rueda real)
  // en el preview, igual que en la pantalla. La página remonta al cambiar de
  // sucursal (key en AdminLayout), así que se re-pide solo.
  const [ruletaSlots, setRuletaSlots] = useState<number | undefined>(undefined);

  // Premios REALES de la ruleta de la sucursal para el preview "dentro del
  // juego" (la rueda real). Se piden al montar (la página remonta al cambiar de
  // sucursal). MOVER_JUEGOS: los visuales de la ruleta (color/grosor/align) y la
  // mecánica de raspadita ya NO se piden por sucursal — ahora son globales y los
  // lee el preview del estado del form de la pestaña Juegos.
  const [ruletaPremios, setRuletaPremios] = useState<PremioPublic[]>([]);

  async function refetchSounds() {
    try {
      const list = await superListSounds();
      setSounds(list);
    } catch {
      // Silencioso — si el endpoint falla, dejamos la lista anterior.
    }
  }

  function refetch() {
    setLoading(true);
    superGetGlobalSettings()
      .then((s) => {
        setSettings(s);
        setLogoUrl(s.logo_url);
        setBgLandscapeUrl(s.bg_landscape_url);
        setBgPortraitUrl(s.bg_portrait_url);
        // SACAR_COLORES (R4): ya no hidratamos color_primary/color_accent.
        // Migration 029: sound_* migran a sucursal.
        setLogoWidthPx(s.logo_width_px);
        setLogoPosition(s.logo_position);
        setCampanaMaxHeightPx(s.campana_max_height_px);
        setCampanaJuegoHeightPx(s.campana_juego_height_px);
        setCampanaJuegoTopMarginPx(s.campana_juego_top_margin_px);
        setJuegoVerticalPosPct(s.juego_vertical_pos_pct);
        setJuegoCanvasDprMax(s.juego_canvas_dpr_max);
        setIconRuletaUrl(s.icon_ruleta_url);
        setIconRaspaditaUrl(s.icon_raspadita_url);
        setIconMemoriaUrl(s.icon_memoria_url);
        setLandingIconSizePx(s.landing_icon_size_px);
        setCampanaToIconosGapPx(s.landing_campana_to_iconos_gap_px);
        setLandingVerticalPosPct(s.landing_vertical_pos_pct);
        setIconosHorizontalAlign(s.landing_iconos_horizontal_align);
        setCampanaTopMarginPx(s.landing_campana_top_margin_px);
        setLogoTopMarginPx(s.landing_logo_top_margin_px);
        setFontHeading(s.font_heading);
        setFontBody(s.font_body);
        setFontDisplay(s.font_display);
        // MOVER_JUEGOS (migration 038): config de juegos global. Los *_habilitada
        // vienen como 0/1 (number) -> a boolean para los toggles.
        setRuletaHab(s.ruleta_habilitada === 1);
        setVelocidadMin(s.ruleta_velocidad_minima);
        setVariacion(s.ruleta_variacion_aleatoria);
        setFrenado(s.ruleta_frenado);
        setUmbralCasi(s.ruleta_umbral_casi_pasa);
        setColorBorde(s.ruleta_color_borde);
        setColorCentro(s.ruleta_color_centro);
        setGrosorDivisoria(s.ruleta_grosor_divisoria);
        setRuletaWheelSizePx(s.ruleta_wheel_size_px);
        setTextAlign(s.ruleta_text_align);
        setRaspaditaHab(s.raspadita_habilitada === 1);
        setMecanica(s.raspadita_mecanica);
        // Migration 039: tamaños del cuerpo de los juegos.
        setRaspaditaPanelWPx(s.raspadita_panel_w_px);
        setRaspaditaPanelHPx(s.raspadita_panel_h_px);
        setMemoriaCartaPx(s.memoria_carta_px);
        setMemoriaHab(s.memoria_habilitada === 1);
        setSegMemFacil(s.memoria_seg_mem_facil);
        setSegMemMedio(s.memoria_seg_mem_medio);
        setSegMemDificil(s.memoria_seg_mem_dificil);
        setMemTFacil(s.memoria_tiempo_facil);
        setMemTMedio(s.memoria_tiempo_medio);
        setMemTDificil(s.memoria_tiempo_dificil);
        // Las 6 imágenes del tablero de memoria (globales, persisten al instante).
        setMemoriaImagenes([
          s.memoria_imagen_1_url,
          s.memoria_imagen_2_url,
          s.memoria_imagen_3_url,
          s.memoria_imagen_4_url,
          s.memoria_imagen_5_url,
          s.memoria_imagen_6_url,
        ]);
        // Migration 041: sonido global. sound_enabled viene como 0/1 (number).
        setSoundEnabled(s.sound_enabled === 1);
        setSoundStyleSlug(s.sound_style_slug);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    refetch();
    refetchSounds();
    // Migration 041: lista de estilos de sonido disponibles para el selector
    // (solo activos). cualquier admin puede leerla. No bloqueante: si falla, el
    // selector queda vacío pero el resto del form sigue funcionando.
    adminListSoundStyles()
      .then(setSoundStyles)
      .catch(() => {});
    // Campaña activa para el preview (no bloqueante: si falla, el preview
    // muestra el fallback "Sin campaña activa", igual que la pantalla real).
    superListCampanas()
      .then((list) => {
        const activa = list.find((c) => c.activa === 1);
        setCampanaActiva(
          activa
            ? { nombre_display: activa.nombre_display, imagen_url: activa.imagen_url }
            : null,
        );
      })
      .catch(() => {});
    // Premios REALES de la ruleta (para los gajos del ícono y la rueda del
    // preview "dentro del juego"). Mismo mapeo AdminProducto -> PremioPublic
    // que Configuracion.
    adminListProductos(sucursalIdEfectiva)
      .then((prods) => {
        const premios: PremioPublic[] = [];
        for (const p of prods) {
          if (p.activo !== 1) continue;
          const j = p.juegos.find((x) => x.juego_type === "ruleta");
          if (!j) continue;
          premios.push({
            id: p.id,
            nombre: p.nombre,
            color: p.color ?? "#888888",
            peso: j.peso,
            is_consolation: p.is_consolation === 1,
            is_reintento: p.is_reintento === 1,
            sort_order: j.sort_order,
          });
        }
        premios.sort((a, b) => a.sort_order - b.sort_order);
        setRuletaPremios(premios);
        setRuletaSlots(premios.length > 0 ? premios.length : undefined);
      })
      .catch(() => {});
    // MOVER_JUEGOS: los visuales de ruleta y la mecánica de raspadita ya no se
    // piden por sucursal acá — son globales y se hidratan en refetch().
    // Precargar las fuentes del catálogo así el grid de selección las
    // muestra correctamente en su propia tipografía. Sin esto, el
    // navegador solo descarga la fuente al activarla con applyFonts → la
    // lista se vería toda en fallback.
    preloadAllFontOptions();
  }, []);

  // Persiste UNA imagen de memoria al instante en global_settings (no espera al
  // "Guardar cambios" del form). El estado local se actualiza de inmediato para
  // feedback; si el PUT falla mostramos error y revertimos. Movido tal cual de
  // Configuracion (las imágenes siempre fueron globales).
  async function persistMemoriaImagen(idx: number, url: string | null) {
    const prev = memoriaImagenes;
    setMemoriaImagenes((arr) => arr.map((u, i) => (i === idx ? url : u)));
    try {
      // Tupla de claves literales (no template string) para que el tipo matchee
      // exacto la union del body de superUpdateGlobalSettings.
      const KEYS = [
        "memoria_imagen_1_url",
        "memoria_imagen_2_url",
        "memoria_imagen_3_url",
        "memoria_imagen_4_url",
        "memoria_imagen_5_url",
        "memoria_imagen_6_url",
      ] as const;
      await superUpdateGlobalSettings({ [KEYS[idx]]: url });
    } catch (e) {
      setMemoriaImagenes(prev);
      dialogs.toast({
        message:
          e instanceof Error
            ? e.message
            : "No se pudo guardar la imagen del tablero.",
        variant: "error",
      });
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await superUpdateGlobalSettings({
        logo_url: logoUrl,
        bg_landscape_url: bgLandscapeUrl,
        bg_portrait_url: bgPortraitUrl,
        // SACAR_COLORES (R4): no enviamos color_primary/color_accent. El
        // backend preserva las columnas (no son nuestro archivo); sacarlas del
        // PUT no las borra — el control no hacía nada visible de todos modos.
        // Migration 029: sound_* ahora se gestionan por sucursal.
        logo_width_px: logoWidthPx,
        logo_position: logoPosition,
        campana_max_height_px: campanaMaxHeightPx,
        campana_juego_height_px: campanaJuegoHeightPx,
        campana_juego_top_margin_px: campanaJuegoTopMarginPx,
        juego_vertical_pos_pct: juegoVerticalPosPct,
        juego_canvas_dpr_max: juegoCanvasDprMax,
        icon_ruleta_url: iconRuletaUrl,
        icon_raspadita_url: iconRaspaditaUrl,
        icon_memoria_url: iconMemoriaUrl,
        landing_icon_size_px: landingIconSizePx,
        landing_campana_to_iconos_gap_px: campanaToIconosGapPx,
        landing_vertical_pos_pct: landingVerticalPosPct,
        landing_iconos_horizontal_align: iconosHorizontalAlign,
        landing_campana_top_margin_px: campanaTopMarginPx,
        landing_logo_top_margin_px: logoTopMarginPx,
        font_heading: fontHeading,
        font_body: fontBody,
        font_display: fontDisplay,
        // MOVER_JUEGOS (migration 038): config de juegos global. Los
        // *_habilitada van como boolean; los enums como string; el resto number.
        ruleta_habilitada: ruletaHab,
        ruleta_velocidad_minima: velocidadMin,
        ruleta_variacion_aleatoria: variacion,
        ruleta_frenado: frenado,
        ruleta_umbral_casi_pasa: umbralCasi,
        ruleta_color_borde: colorBorde,
        ruleta_color_centro: colorCentro,
        ruleta_grosor_divisoria: grosorDivisoria,
        ruleta_text_align: textAlign,
        // Migration 044: tamaño de la rueda.
        ruleta_wheel_size_px: ruletaWheelSizePx,
        raspadita_habilitada: raspaditaHab,
        raspadita_mecanica: mecanica,
        // Autocura cualquier valor legacy (única mecánica sana, ver sim-mecanicas).
        raspadita_grid_dinamica: "encontrar",
        // Migration 039: tamaños del cuerpo de los juegos.
        raspadita_panel_w_px: raspaditaPanelWPx,
        raspadita_panel_h_px: raspaditaPanelHPx,
        memoria_carta_px: memoriaCartaPx,
        memoria_habilitada: memoriaHab,
        memoria_seg_mem_facil: segMemFacil,
        memoria_seg_mem_medio: segMemMedio,
        memoria_seg_mem_dificil: segMemDificil,
        memoria_tiempo_facil: memTFacil,
        memoria_tiempo_medio: memTMedio,
        memoria_tiempo_dificil: memTDificil,
        // Las 6 imágenes de memoria NO van acá: se persisten al instante vía
        // persistMemoriaImagen (sus columnas las preserva el backend si se omiten).
        // Migration 041: sonido global (estilo + habilitado).
        sound_enabled: soundEnabled,
        sound_style_slug: soundStyleSlug,
      });
      // Aplicar las tipografías al admin SOLO cuando se guarda
      // exitosamente. Antes el FontPicker aplicaba en vivo a cada
      // cambio y eso "agarraba todo el admin", lo que confundía al
      // user (la lista del selector también cambiaba a la nueva
      // fuente). Ahora la selección es local hasta confirmar.
      applyFonts({
        heading: fontHeading,
        body: fontBody,
        display: fontDisplay,
      });
      setPageDirty(false);
      dialogs.toast({
        message: "Guardado. Las pantallas verán los cambios al recargar.",
        variant: "success",
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading || !settings) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin opacity-50" />
      </div>
    );
  }

  // Qué preview va a la derecha:
  //  - "juego": Distribución › Dentro del juego (la ruleta real) + pestaña
  //    Juegos (el juego de la sub-pestaña activa).
  //  - "landing": Pantalla principal, Distribución › Pantalla principal.
  //  - "none": Sonidos (sin preview).
  const previewMode: "landing" | "juego" | "none" =
    activeTab === "sonidos"
      ? "none"
      : activeTab === "juegos"
      ? "juego"
      : activeTab === "distribucion" && distribSub === "juego"
      ? "juego"
      : "landing";
  const showPreview = previewMode !== "none";
  // En la pestaña Juegos el preview sigue la sub-pestaña activa
  // (Ruleta/Raspadita/Memoria); en Distribución › Dentro del juego deja que el
  // selector interno del JuegoPreview elija. Si el juego de la sub-pestaña está
  // OFF, no hay pantalla real -> mostramos el placeholder en vez del cuerpo.
  const juegosPreviewActivo =
    activeTab === "juegos"
      ? activeJuegoTab === "ruleta"
        ? ruletaHab
        : activeJuegoTab === "raspadita"
        ? raspaditaHab
        : memoriaHab
      : true;
  const juegosPreviewNombre =
    activeJuegoTab === "ruleta"
      ? "Ruleta"
      : activeJuegoTab === "raspadita"
      ? "Raspadita"
      : "Memoria";

  return (
    <div>
      {/* Titulo visible removido (pedido de ahorro de espacio): el sidebar ya
          marca "Ajustes del shopping". Queda solo como heading accesible para
          el outline del documento, sin gastar alto vertical. */}
      <h1 className="sr-only">Ajustes del shopping</h1>

      {/* PREVIEW FIJO (riel derecho). En lg+ es position:fixed, a todo el alto
          del viewport (hasta arriba) y NO se mueve con el scroll de los
          controles. En mobile vuelve al flujo normal (arriba de los controles).
          Se muestra en Pantalla principal y Distribucion; en Sonidos no hay. */}
      {showPreview && (
        <div className="mb-4 flex h-[70vh] justify-center lg:fixed lg:right-0 lg:top-0 lg:z-10 lg:mb-0 lg:h-screen lg:w-[34vw] lg:p-3 xl:w-[31vw]">
          {previewMode === "juego" ? (
            !juegosPreviewActivo ? (
              // Pestaña Juegos con el juego de la sub-pestaña deshabilitado.
              <GamePreviewFrame bgUrl={bgPortraitUrl}>
                <PreviewDeshabilitado nombre={juegosPreviewNombre} />
              </GamePreviewFrame>
            ) : (
            <JuegoPreview
              // En Juegos el preview lo controla la sub-pestaña (sin selector
              // interno); en Distribución › Dentro del juego, el selector manda.
              juego={activeTab === "juegos" ? activeJuegoTab : undefined}
              showSelector={activeTab !== "juegos"}
              bgUrl={bgPortraitUrl}
              campana={campanaActiva}
              campanaJuegoHeightPx={campanaJuegoHeightPx}
              campanaJuegoTopMarginPx={campanaJuegoTopMarginPx}
              premios={ruletaPremios}
              colorBorde={colorBorde}
              colorCentro={colorCentro}
              grosorDivisoria={grosorDivisoria}
              textAlign={textAlign}
              juegoVerticalPosPct={juegoVerticalPosPct}
              raspaditaMecanica={mecanica}
              raspaditaIconUrl={iconRaspaditaUrl}
              raspaditaPanelW={raspaditaPanelWPx}
              raspaditaPanelH={raspaditaPanelHPx}
              memoriaImagenes={memoriaImagenes}
              memoriaCardSize={memoriaCartaPx}
              canvasDprMax={juegoCanvasDprMax}
              ruletaWheelSize={ruletaWheelSizePx}
              fontHeading={fontHeading}
              fontDisplay={fontDisplay}
            />
            )
          ) : (
          <PantallaPreview
            bgUrl={bgPortraitUrl}
            campana={campanaActiva}
            logoUrl={logoUrl}
            logoWidthPx={logoWidthPx}
            logoPosition={logoPosition}
            logoTopMarginPx={logoTopMarginPx}
            campanaMaxHeightPx={campanaMaxHeightPx}
            campanaTopMarginPx={campanaTopMarginPx}
            iconRuletaUrl={iconRuletaUrl}
            iconRaspaditaUrl={iconRaspaditaUrl}
            iconMemoriaUrl={iconMemoriaUrl}
            iconSizePx={landingIconSizePx}
            gapPx={campanaToIconosGapPx}
            verticalPosPct={landingVerticalPosPct}
            hAlign={iconosHorizontalAlign}
            fontHeading={fontHeading}
            fontBody={fontBody}
            fontDisplay={fontDisplay}
            ruletaSlots={ruletaSlots}
          />
          )}
        </div>
      )}

      {/* Contenido. Reserva ancho a la derecha (lg+) cuando hay preview, asi la
          linea de las pestañas, las cards y la barra Guardar NO quedan debajo
          del riel fijo. */}
      <div className={showPreview ? "lg:pr-[calc(34vw_-_1.5rem)] xl:pr-[calc(31vw_-_1.5rem)]" : ""}>
      <div
        className="mb-4 flex gap-1 overflow-x-auto border-b border-white/10"
        data-tour="global-tabs"
      >
        {TABS.map(({ key, label, Icon }) => (
          <button
            key={key}
            type="button"
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium transition ${
              activeTab === key
                ? "border-[#a78bfa] text-white"
                : "border-transparent text-white/60 hover:text-white"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      <form
        onSubmit={handleSave}
        onChange={() => setPageDirty(true)}
        className="flex flex-col gap-6"
      >
        {activeTab === "pantalla" && (
          // Masonry (columns) en vez de grid: las cards se empaquetan denso de
          // arriba a abajo, sin los huecos que dejaba el grid cuando una card
          // era más alta que su vecina (pedido: nada de espacios vacíos). Cada
          // card no se parte entre columnas (break-inside-avoid).
          <div className="columns-1 gap-4 xl:columns-2 [&>*]:mb-4 [&>*]:break-inside-avoid">
        {/* UNIR_MARCA_PANTALLA (R4): la card "Logo del shopping" venía de la
            vieja pestaña Marca. Ahora vive en Pantalla principal junto al resto
            del landing. */}
        {/* SUMMARY_REDUNDANTE: se quitó el summary "{ancho} px · posición" —
            duplicaba el slider + los botones de posición de abajo. El dueño
            pidió no repetir arriba lo que los controles ya muestran. */}
        <SettingCard title="Logo del shopping">
          {/* LOGO_PREVIEW_REDUNDANTE: se quitó el LogoPreview "Vista previa en
              vivo" de esta card. El logo ya se ve a la derecha en el
              PantallaPreview grande, al ancho y posición elegidos; tenerlo
              también acá lo mostraba dos veces. La card queda solo con el
              uploader + los controles de tamaño/posición. showThumbnail={false}
              porque la imagen ya manda en el preview lateral. */}
          <AssetUploader
            kind="logo"
            currentUrl={logoUrl}
            onUploaded={setLogoUrl}
            onClear={() => setLogoUrl(null)}
            showThumbnail={false}
          />

          <div className="mt-5 border-t border-white/10 pt-5">
            {/* El ANCHO del logo se ajusta en Distribución › Pantalla principal
                (todos los tamaños viven juntos). Acá solo la posición. */}
            {/* Posición — 3 botones radio con íconos */}
            <div className="flex flex-col gap-2">
              <span className="text-sm opacity-80">Posición en la pantalla</span>
              <div className="grid grid-cols-3 gap-2">
                {([
                  { value: "left" as LogoPosition, label: "Izquierda", Icon: AlignLeft },
                  { value: "center" as LogoPosition, label: "Centro", Icon: AlignCenter },
                  { value: "right" as LogoPosition, label: "Derecha", Icon: AlignRight },
                ]).map(({ value, label, Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setLogoPosition(value)}
                    className={`flex flex-col items-center gap-1 rounded-lg border-2 px-3 py-2 text-sm transition ${
                      logoPosition === value
                        ? "border-[#a78bfa] bg-[#a78bfa]/10"
                        : "border-white/10 hover:bg-white/5"
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    <span className="text-xs">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </SettingCard>

        {/* UNIR_MARCA_PANTALLA (R4): "Tipografías" también venía de Marca. */}
        {/* SUMMARY_REDUNDANTE: los badges H/B/D de arriba duplicaban los
            FontPicker de abajo (cada uno ya muestra la fuente elegida). */}
        <SettingCard
          title="Tipografías"
          help={
            "3 slots semánticos.\n\nHeading = títulos grandes (modal del premio, landing).\nBody = texto general (descripciones, formularios).\nDisplay = texto promocional (botón GIRAR, nombres de premios en el wheel, banners).\n\nLos cambios se aplican a todas las pantallas al guardar."
          }
        >
          <div className="flex flex-col gap-4">
            <FontPicker
              label="Heading (títulos)"
              slot="heading"
              value={fontHeading}
              onChange={setFontHeading}
            />
            <FontPicker
              label="Body (texto general)"
              slot="body"
              value={fontBody}
              onChange={setFontBody}
            />
            <FontPicker
              label="Display (texto promocional)"
              slot="display"
              value={fontDisplay}
              onChange={setFontDisplay}
            />
          </div>
        </SettingCard>

        {/* Los tamaños (campaña portada, botones, logo) se movieron a
            Distribución › Pantalla principal (pedido: todo lo de tamaño junto). */}

        {/* FONDOS_UNIFICADOS: el dueño pidió una sola card "Fondos" con el
            horizontal y el vertical adentro, en vez de dos cards ocupando el
            doble de espacio. */}
        <SettingCard title="Fondos">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <div className="mb-2 text-sm font-medium opacity-80">
                Horizontal (landscape)
              </div>
              <AssetUploader
                kind="bg-landscape"
                currentUrl={bgLandscapeUrl}
                onUploaded={setBgLandscapeUrl}
                onClear={() => setBgLandscapeUrl(null)}
              />
            </div>
            <div>
              <div className="mb-2 text-sm font-medium opacity-80">
                Vertical (portrait)
              </div>
              <AssetUploader
                kind="bg-portrait"
                currentUrl={bgPortraitUrl}
                onUploaded={setBgPortraitUrl}
                onClear={() => setBgPortraitUrl(null)}
              />
            </div>
          </div>
        </SettingCard>

        {/* Reorg IA 2026-06: los íconos de los botones de juego se
            movieron de la pestaña Marca a Pantalla principal, junto al
            tamaño de los botones — todo lo que afecta cómo se ven los
            botones de la pantalla vive ahora en el mismo lugar. */}
        {/* SUMMARY_REDUNDANTE: el summary "Ruleta: por defecto · ..." se
            quitó; cada uploader ya muestra si hay ícono cargado o "Sin
            imagen". Migration 033: se agregó el ícono de Memoria. */}
        <SettingCard
          title="Íconos de los botones de juego"
          help={
            "Reemplazan los íconos por defecto (dados / goma / cerebro) dentro de los botones Ruleta, Raspadita y Memoria de la pantalla principal. Útil para que combinen con la temática de la campaña (pelota para Mundial, huevo para Pascua, etc.). Si los quitás, vuelven los íconos por defecto."
          }
        >
          <div className="grid grid-cols-1 items-start gap-4 md:grid-cols-3">
            <div>
              <div className="mb-2 truncate text-sm font-medium opacity-80">
                Ruleta
              </div>
              <AssetUploader
                kind="icon-ruleta"
                currentUrl={iconRuletaUrl}
                onUploaded={setIconRuletaUrl}
                onClear={() => setIconRuletaUrl(null)}
              />
            </div>
            <div>
              <div className="mb-2 truncate text-sm font-medium opacity-80">
                Raspadita
              </div>
              <AssetUploader
                kind="icon-raspadita"
                currentUrl={iconRaspaditaUrl}
                onUploaded={setIconRaspaditaUrl}
                onClear={() => setIconRaspaditaUrl(null)}
              />
            </div>
            <div>
              <div className="mb-2 truncate text-sm font-medium opacity-80">
                Memoria
              </div>
              <AssetUploader
                kind="icon-memoria"
                currentUrl={iconMemoriaUrl}
                onUploaded={setIconMemoriaUrl}
                onClear={() => setIconMemoriaUrl(null)}
              />
            </div>
          </div>
        </SettingCard>

        {/* MOVER_MEMORIA: la card "Imágenes del juego Memoria" (6 uploaders
            memoria-1..6) se eliminó de acá. Se gestiona ahora en Mi pantalla ›
            Juegos › Memoria (otro agente). El estado memoriaImagenes y los
            AssetKind memoria-* ya no se usan en este archivo: se quitaron del
            form state y del PUT para no dejar codigo muerto. */}

        {/* DISTRIBUCION: la card "Distribución del menú principal" vive en la
            pestaña "Distribución" (junto a la campaña en-juego). */}
        </div>
        )}

        {/* PESTAÑA_PANTALLA_DE_JUEGO (R4): lo que ve el cliente al ENTRAR a un
            juego (ruleta/raspadita/memoria) — el fondo vertical (portrait) con
            la imagen de campaña en el header. El único control de esta pantalla
            es el alto de esa campaña (campana_juego_height_px), que antes vivía
            mezclado en la card de campaña de la portada. Mismo patrón que
            Pantalla principal: controles a la izquierda, preview grande sticky a
            la derecha que refleja el slider al instante. */}
        {activeTab === "distribucion" && (
          <div>
            {/* Sub-pestañas: Pantalla principal (landing) / Dentro del juego.
                El preview de la derecha cambia según cuál esté activa. */}
            <div className="mb-4 flex gap-1 overflow-x-auto rounded-lg bg-white/5 p-1">
              {([
                { key: "principal" as const, label: "Pantalla principal" },
                { key: "juego" as const, label: "Dentro del juego" },
              ]).map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setDistribSub(key)}
                  className={`flex-1 rounded-md px-3 py-2 text-sm font-medium transition ${
                    distribSub === key
                      ? "bg-[#a78bfa]/20 text-white"
                      : "text-white/60 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {distribSub === "principal" && (
              <div className="columns-1 gap-4 xl:columns-2 [&>*]:mb-4 [&>*]:break-inside-avoid">
              {/* Todos los TAMAÑOS de la pantalla principal juntos (pedido). */}
              <SettingCard title="Tamaños (pantalla principal)">
                <SliderField
                  label="Ancho del logo"
                  value={logoWidthPx}
                  onChange={setLogoWidthPx}
                  min={LOGO_MIN_PX}
                  max={LOGO_MAX_PX}
                  step={10}
                  unit="px"
                  defaultValue={settings.logo_width_px}
                />
                <SliderField
                  label="Alto de la campaña en la portada"
                  value={campanaMaxHeightPx}
                  onChange={setCampanaMaxHeightPx}
                  min={CAMPANA_MIN_PX}
                  max={CAMPANA_MAX_PX}
                  step={10}
                  unit="px"
                  defaultValue={settings.campana_max_height_px}
                />
                <SliderField
                  label="Tamaño de los botones de juego"
                  value={landingIconSizePx}
                  onChange={setLandingIconSizePx}
                  min={100}
                  max={500}
                  step={8}
                  unit="px"
                  minLabel="Chico 100"
                  maxLabel="Grande 500"
                  defaultValue={settings.landing_icon_size_px}
                />
              </SettingCard>

              {/* La distribución del menú (landing). El preview grande la refleja. */}
              <SettingCard
                title="Posición (pantalla principal)"
                help="Dónde se ubican el logo, la campaña y los botones de juego. Ordenado de arriba hacia abajo, como en la pantalla. Mirá el preview de la derecha mientras ajustás."
              >
                {/* Reorg 2026-06-26: todos los controles de "mover cosas" del
                    landing juntos y en orden de arriba hacia abajo (logo →
                    campaña → íconos), para que sea fácil de seguir. */}
                {/* Migration 044: aire arriba del LOGO (lo baja desde el borde
                    superior de la pantalla). */}
                <SliderField
                  label="Aire arriba del logo"
                  value={logoTopMarginPx}
                  onChange={setLogoTopMarginPx}
                  min={LOGO_TOP_MARGIN_MIN_PX}
                  max={LOGO_TOP_MARGIN_MAX_PX}
                  step={4}
                  unit="px"
                  help="Baja el logo desde el borde superior de la pantalla."
                  defaultValue={settings.landing_logo_top_margin_px}
                />
                <SliderField
                  label="Aire arriba de la campaña"
                  value={campanaTopMarginPx}
                  onChange={setCampanaTopMarginPx}
                  min={0}
                  max={600}
                  step={4}
                  unit="px"
                  help="Distancia extra entre el logo y la campaña."
                  defaultValue={settings.landing_campana_top_margin_px}
                />
                <SliderField
                  label="Espacio entre campaña e íconos"
                  value={campanaToIconosGapPx}
                  onChange={setCampanaToIconosGapPx}
                  min={0}
                  max={600}
                  step={4}
                  unit="px"
                  minLabel="Pegados 0"
                  maxLabel="Separados 600"
                  defaultValue={settings.landing_campana_to_iconos_gap_px}
                />
                {/* Migration 045: posición vertical FINA (deslizador 0..100%),
                    el MISMO sistema que "Dentro del juego". Reemplaza los 3
                    botones Arriba/Centro/Abajo -> ahora subís la campaña+íconos
                    tanto como quieras, sin recortarse. */}
                <SliderField
                  label="Posición vertical (arriba ↔ abajo)"
                  value={landingVerticalPosPct}
                  onChange={setLandingVerticalPosPct}
                  min={0}
                  max={100}
                  step={1}
                  unit="%"
                  minLabel="Arriba"
                  maxLabel="Abajo"
                  help="Sube o baja el bloque de campaña + íconos dentro de la pantalla. 0% = arriba (bajo el logo), 50% = centro, 100% = abajo."
                  defaultValue={settings.landing_vertical_pos_pct}
                />
                <div>
                  <div className="mb-2 text-sm opacity-80">
                    Alineación horizontal de los íconos
                  </div>
                  <div className="flex gap-1 rounded-lg bg-white/5 p-1">
                    {(["left", "center", "right"] as const).map((opt) => (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setIconosHorizontalAlign(opt)}
                        className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium transition ${
                          iconosHorizontalAlign === opt
                            ? "bg-[#a78bfa]/20 text-white"
                            : "text-white/60 hover:bg-white/5"
                        }`}
                      >
                        {opt === "left" ? (
                          <AlignLeft className="h-3.5 w-3.5" />
                        ) : opt === "right" ? (
                          <AlignRight className="h-3.5 w-3.5" />
                        ) : (
                          <AlignCenter className="h-3.5 w-3.5" />
                        )}
                        {opt === "left" ? "Izquierda" : opt === "right" ? "Derecha" : "Centro"}
                      </button>
                    ))}
                  </div>
                </div>
              </SettingCard>
              </div>
            )}

            {distribSub === "juego" && (
              <div className="grid grid-cols-1 items-start gap-4">
              <SettingCard
                title="Campaña dentro del juego"
                help="Tamaño y posición de la imagen de campaña en el header de cada juego (ruleta/raspadita/memoria), la primera pantalla al entrar. Mirá el preview de la ruleta a la derecha."
              >
                {/* Migration 034: alto dentro del juego. Antes hardcodeado a
                    80px; el dueño la veía muy chica al entrar. */}
                <SliderField
                  label="Alto dentro del juego"
                  value={campanaJuegoHeightPx}
                  onChange={setCampanaJuegoHeightPx}
                  min={CAMPANA_JUEGO_MIN_PX}
                  max={CAMPANA_JUEGO_MAX_PX}
                  step={5}
                  unit="px"
                  defaultValue={settings.campana_juego_height_px}
                />

                {/* Migration 035: aire-arriba dentro del juego. Le da a la
                    pestaña un 2do ajuste (el dueño se quejó de que tenía uno
                    solo) y permite bajar la campaña cuando queda muy pegada al
                    borde superior del juego. */}
                <SliderField
                  label="Aire arriba en el juego"
                  value={campanaJuegoTopMarginPx}
                  onChange={setCampanaJuegoTopMarginPx}
                  min={CAMPANA_JUEGO_TOP_MARGIN_MIN_PX}
                  max={CAMPANA_JUEGO_TOP_MARGIN_MAX_PX}
                  step={5}
                  unit="px"
                  help="Empuja la campaña hacia abajo en la pantalla del juego. Útil cuando queda muy pegada al borde de arriba."
                  defaultValue={settings.campana_juego_top_margin_px}
                />

                {/* Migration 037: posición vertical del cuerpo del juego (la
                    rueda / grilla / tablero), 0..100%. Deslizador FINO (pedido
                    del dueño, no 3 botones): 0 = pegado a la campaña, 50 =
                    centro, 100 = abajo. La rueda nunca se recorta (espaciadores
                    flex en el RuletaView). */}
                <SliderField
                  label="Posición del juego (arriba ↔ abajo)"
                  value={juegoVerticalPosPct}
                  onChange={setJuegoVerticalPosPct}
                  min={0}
                  max={100}
                  step={1}
                  unit="%"
                  help="Sube o baja la rueda / grilla dentro de la pantalla, debajo de la campaña. 0% = arriba, 50% = centro, 100% = abajo."
                  defaultValue={settings.juego_vertical_pos_pct}
                />
              </SettingCard>

              {/* Reubicación 2026-06-27: el TAMAÑO del cuerpo de cada juego
                  (rueda / panel de raspar / cartas) vivía disperso en Juegos ›
                  cada juego, donde el dueño no lo encontraba ("agrandar la
                  ruleta"). Movido acá, a Distribución › Dentro del juego, junto
                  al resto del layout en-juego, consolidado y por juego. */}
              <SettingCard
                title="Tamaño del juego"
                help="Qué tan grande se ve el cuerpo de cada juego (la rueda, el panel de raspar, las cartas) en la pantalla. Cambiá de juego con el selector de arriba del preview para ver cada uno. Si la pantalla es más chica, se ajusta solo para no recortarse."
              >
                <SliderField
                  label="Rueda de la ruleta"
                  value={ruletaWheelSizePx}
                  onChange={setRuletaWheelSizePx}
                  defaultValue={settings.ruleta_wheel_size_px}
                  min={RULETA_WHEEL_MIN_PX}
                  max={RULETA_WHEEL_MAX_PX}
                  step={10}
                  unit="px"
                  minLabel="Chica"
                  maxLabel="Grande"
                />
                <SliderField
                  label="Raspadita — ancho del panel"
                  value={raspaditaPanelWPx}
                  onChange={setRaspaditaPanelWPx}
                  min={RASPADITA_PANEL_MIN_PX}
                  max={RASPADITA_PANEL_MAX_PX}
                  step={10}
                  unit="px"
                  minLabel="Angosto"
                  maxLabel="Ancho"
                  defaultValue={settings.raspadita_panel_w_px}
                />
                <SliderField
                  label="Raspadita — alto del panel"
                  value={raspaditaPanelHPx}
                  onChange={setRaspaditaPanelHPx}
                  min={RASPADITA_PANEL_MIN_PX}
                  max={RASPADITA_PANEL_MAX_PX}
                  step={10}
                  unit="px"
                  minLabel="Bajo"
                  maxLabel="Alto"
                  defaultValue={settings.raspadita_panel_h_px}
                />
                <SliderField
                  label="Cartas de memoria"
                  value={memoriaCartaPx}
                  onChange={setMemoriaCartaPx}
                  min={MEMORIA_CARTA_MIN_PX}
                  max={MEMORIA_CARTA_MAX_PX}
                  step={5}
                  unit="px"
                  minLabel="Chicas"
                  maxLabel="Grandes"
                  defaultValue={settings.memoria_carta_px}
                />
              </SettingCard>

              {/* Migration 040: nitidez de los canvas (ruleta + raspadita). Es
                  cross-juego, por eso vive acá (no en una sub-pestaña de
                  Juegos). Subir = más nítido en pantallas 4K; bajar = más
                  fluido si la rueda va lenta. */}
              <SettingCard
                title="Nitidez de los juegos"
                help="Cuánta resolución usan la ruleta y la raspadita. Más nitidez se ve mejor en pantallas grandes (4K) pero puede ir más lento; si notás la rueda trabada, bajala. Probalo en la pantalla real."
              >
                <SliderField
                  label="Nitidez del canvas"
                  value={juegoCanvasDprMax}
                  onChange={setJuegoCanvasDprMax}
                  min={1}
                  max={3}
                  step={0.5}
                  displayDecimals={1}
                  unit="×"
                  minLabel="Más fluido"
                  maxLabel="Más nítido"
                  defaultValue={settings.juego_canvas_dpr_max}
                />
              </SettingCard>
              </div>
            )}
          </div>
        )}

        {/* ──────────── PESTAÑA JUEGOS ──────────── */}
        {/* MOVER_JUEGOS (migration 038): el config de juegos pasó de por-sucursal
            (Mi pantalla › Juegos) a GLOBAL del shopping. Mismas sub-pestañas
            (Ruleta/Raspadita/Memoria) y mismos controles que tenía Configuracion,
            cableados ahora a global_settings. El preview del juego activo vive en
            el riel fijo de la derecha (ya reservado por previewMode "juego"). */}
        {activeTab === "juegos" && (
          <div className="flex flex-col gap-6">
            {/* Sub-pestañas — Ruleta / Raspadita / Memoria. Visibilidad por CSS
                para mantener el estado del form al cambiar de juego (se guardan
                todos juntos con el "Guardar cambios" del pie). */}
            <div className="flex gap-1 overflow-x-auto rounded-lg bg-white/5 p-1">
              {JUEGO_TABS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveJuegoTab(key)}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition ${
                    activeJuegoTab === key
                      ? "bg-[#a78bfa]/20 text-white"
                      : "text-white/60 hover:bg-white/5 hover:text-white"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* RULETA */}
            <div className={activeJuegoTab === "ruleta" ? "" : "hidden"}>
              <Section>
                <ToggleRow
                  checked={ruletaHab}
                  onChange={setRuletaHab}
                  label="Habilitar ruleta"
                  desc="Si está OFF, el botón Ruleta no aparece en la pantalla."
                />

                {ruletaHab && (
                  <>
                    {/* Visuales */}
                    <div className="mt-4 rounded-lg bg-black/20 p-3">
                      <div className="mb-3 text-sm font-medium opacity-80">
                        Aspecto visual
                      </div>
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <ColorField
                          label="Color del anillo (borde)"
                          value={colorBorde}
                          onChange={setColorBorde}
                          help="Color del marco decorativo + divisorias entre premios."
                        />
                        <ColorField
                          label="Color del centro (botón GIRAR)"
                          value={colorCentro}
                          onChange={setColorCentro}
                          help="Color del botón central."
                        />
                      </div>
                      <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <SliderField
                            label="Grosor de divisorias"
                            value={grosorDivisoria}
                            onChange={setGrosorDivisoria}
                            defaultValue={settings.ruleta_grosor_divisoria}
                            min={1}
                            max={8}
                            step={1}
                            unit="px"
                            minLabel="Finas"
                            maxLabel="Gruesas"
                            help="Qué tan gruesas son las líneas entre cada segmento. Mirá el preview de la derecha."
                          />
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <label className="text-sm font-medium">
                              Alineación del texto del premio
                            </label>
                            <HelpPopover>
                              Dónde se ubica el nombre del premio dentro de su
                              gajo.
                            </HelpPopover>
                          </div>
                          <div className="mt-2 flex gap-1">
                            {(
                              [
                                { v: "left" as const, Icon: AlignLeft },
                                { v: "center" as const, Icon: AlignCenter },
                                { v: "right" as const, Icon: AlignRight },
                              ]
                            ).map(({ v, Icon }) => (
                              <button
                                key={v}
                                type="button"
                                onClick={() => {
                                  setTextAlign(v);
                                  setPageDirty(true);
                                }}
                                className={`flex-1 rounded-lg border-2 px-3 py-2 transition ${
                                  textAlign === v
                                    ? "border-[#a78bfa] bg-[#a78bfa]/10"
                                    : "border-white/10 hover:bg-white/5"
                                }`}
                                aria-label={v}
                              >
                                <Icon className="mx-auto h-4 w-4" />
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* El TAMAÑO de la rueda se movió a Distribución › Dentro
                        del juego › "Tamaño del juego" (junto al resto del
                        layout en-juego). */}

                    {/* Físicos */}
                    <div className="mt-4 rounded-lg bg-black/20 p-3">
                      <div className="mb-3 flex items-center gap-1.5 text-sm font-medium opacity-80">
                        Física del giro
                        <HelpPopover>
                          Ajustá cómo se siente el giro. Los defaults funcionan
                          bien para la mayoría de casos.
                        </HelpPopover>
                      </div>
                      <SliderField
                        label="Velocidad mínima"
                        value={velocidadMin}
                        onChange={setVelocidadMin}
                        defaultValue={settings.ruleta_velocidad_minima}
                        min={0.1}
                        max={1.2}
                        step={0.05}
                        displayDecimals={2}
                        minLabel="Lento"
                        maxLabel="Rápido"
                        help="Qué tan rápido gira siempre, sin importar el azar."
                      />
                      <SliderField
                        label="Variación aleatoria"
                        value={variacion}
                        onChange={setVariacion}
                        defaultValue={settings.ruleta_variacion_aleatoria}
                        min={0}
                        max={1.5}
                        step={0.05}
                        displayDecimals={2}
                        minLabel="Sin variación"
                        maxLabel="Muy variable"
                        help="Velocidad extra aleatoria sumada al mínimo en cada giro."
                      />
                      <SliderField
                        label="Frenado"
                        value={frenado}
                        onChange={setFrenado}
                        defaultValue={settings.ruleta_frenado}
                        min={0.96}
                        max={0.999}
                        step={0.001}
                        displayDecimals={3}
                        minLabel="Frena rápido"
                        maxLabel="Gira largo"
                        help="Qué tan rápido para la rueda. Más alto = frena más lento."
                      />
                      <SliderField
                        label="Umbral 'casi pasa'"
                        value={umbralCasi}
                        onChange={setUmbralCasi}
                        defaultValue={settings.ruleta_umbral_casi_pasa}
                        min={0.05}
                        max={0.45}
                        step={0.01}
                        displayAsPercent
                        minLabel="Solo si toca"
                        maxLabel="Zona amplia"
                        help="Qué tan cerca del punto debe quedar para activar la animación dramática."
                      />
                    </div>
                  </>
                )}
              </Section>
            </div>

            {/* RASPADITA */}
            <div className={activeJuegoTab === "raspadita" ? "" : "hidden"}>
              <Section>
                <ToggleRow
                  checked={raspaditaHab}
                  onChange={setRaspaditaHab}
                  label="Habilitar raspadita"
                  desc="Si está OFF, el botón Raspadita no aparece en la pantalla."
                />

                {raspaditaHab && (
                  <div className="mt-4 rounded-lg bg-black/20 p-3">
                    <label className="text-sm font-medium">
                      Mecánica de la raspadita
                    </label>
                    <select
                      value={mecanica}
                      onChange={(e) =>
                        setMecanica(e.target.value as RaspaditaMecanica)
                      }
                      className="mt-1 w-full rounded-lg bg-white/10 px-3 py-2 outline-none"
                    >
                      <option value="panel">
                        Panel grande (raspás una sola área)
                      </option>
                      <option value="grid">
                        Grid 9 casillas (encontrar 3 iguales)
                      </option>
                      <option value="elije">
                        Elije tu raspadita (3 tarjetitas, una se agranda)
                      </option>
                    </select>
                    {mecanica === "grid" && (
                      <div className="mt-3 flex items-center gap-1.5 text-xs opacity-60">
                        El cliente raspa casillas hasta encontrar 3 iguales.
                        <HelpPopover>
                          El premio que aparece SIEMPRE es el que eligió el
                          servidor; el raspado es solo la animación de
                          descubrirlo.
                        </HelpPopover>
                      </div>
                    )}
                  </div>
                )}
                {/* El TAMAÑO del panel de raspar (ancho/alto) se movió a
                    Distribución › Dentro del juego › "Tamaño del juego". */}
              </Section>
            </div>

            {/* MEMORIA */}
            <div className={activeJuegoTab === "memoria" ? "" : "hidden"}>
              <Section>
                <ToggleRow
                  checked={memoriaHab}
                  onChange={setMemoriaHab}
                  label="Habilitar memoria"
                  desc="Juego de pares (3×4). El cliente elige nivel + premio antes de jugar."
                />

                {memoriaHab && (
                  <>
                    {/* El TAMAÑO de las cartas se movió a Distribución › Dentro
                        del juego › "Tamaño del juego". */}

                    {/* Imágenes del tablero — globales del shopping. Se guardan
                        al instante al subir/quitar (no esperan al "Guardar
                        cambios"). */}
                    <div className="mt-4 rounded-lg bg-black/20 p-3">
                      <div className="mb-1 flex items-center gap-1.5 text-sm font-medium opacity-80">
                        Imágenes del tablero (6 pares)
                        <HelpPopover>
                          Cada imagen aparece 2 veces en el tablero (6 pares = 12
                          casillas). Se guardan al instante al subir o quitar.
                          SVG ideal para que no pixele en TV 4K.
                        </HelpPopover>
                      </div>
                      <p className="mb-3 text-xs opacity-60">
                        Si faltan, el juego no arranca aunque esté habilitado.
                      </p>
                      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
                        {memoriaImagenes.map((url, i) => {
                          const slot = i + 1;
                          const kind = `memoria-${slot}` as AssetKind;
                          return (
                            <div key={slot}>
                              <div className="mb-2 text-sm font-medium opacity-80">
                                Imagen {slot}
                              </div>
                              <AssetUploader
                                kind={kind}
                                currentUrl={url}
                                onUploaded={(newUrl) =>
                                  persistMemoriaImagen(i, newUrl)
                                }
                                onClear={() => persistMemoriaImagen(i, null)}
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Tiempos de memorización por nivel */}
                    <div className="mt-4 rounded-lg bg-black/20 p-3">
                      <div className="mb-3 flex items-center gap-1.5 text-sm font-medium opacity-80">
                        Tiempo de memorización (al inicio)
                        <HelpPopover>
                          Segundos que el tablero queda revelado para memorizar.
                          Difícil debería ser más corto que fácil. Acepta
                          sub-segundos (ej: 0.8). Rango 0.1 – 30 s.
                        </HelpPopover>
                      </div>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="opacity-80">Fácil (s)</span>
                          <NumberField
                            value={segMemFacil}
                            onChange={setSegMemFacil}
                            min={0.1}
                            max={30}
                            step={0.1}
                            ariaLabel="Tiempo de memorización fácil (segundos)"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="opacity-80">Medio (s)</span>
                          <NumberField
                            value={segMemMedio}
                            onChange={setSegMemMedio}
                            min={0.1}
                            max={30}
                            step={0.1}
                            ariaLabel="Tiempo de memorización medio (segundos)"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="opacity-80">Difícil (s)</span>
                          <NumberField
                            value={segMemDificil}
                            onChange={setSegMemDificil}
                            min={0.1}
                            max={30}
                            step={0.1}
                            ariaLabel="Tiempo de memorización difícil (segundos)"
                          />
                        </label>
                      </div>
                    </div>

                    <div className="mt-4 rounded-lg bg-black/20 p-3">
                      <div className="mb-3 flex items-center gap-1.5 text-sm font-medium opacity-80">
                        Tiempo para resolver (jugar)
                        <HelpPopover>
                          Segundos para encontrar los pares.
                        </HelpPopover>
                      </div>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="opacity-80">Fácil (s)</span>
                          <NumberField
                            value={memTFacil}
                            onChange={setMemTFacil}
                            min={5}
                            max={180}
                            step={1}
                            ariaLabel="Tiempo para resolver fácil (segundos)"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="opacity-80">Medio (s)</span>
                          <NumberField
                            value={memTMedio}
                            onChange={setMemTMedio}
                            min={5}
                            max={180}
                            step={1}
                            ariaLabel="Tiempo para resolver medio (segundos)"
                          />
                        </label>
                        <label className="flex flex-col gap-1 text-sm">
                          <span className="opacity-80">Difícil (s)</span>
                          <NumberField
                            value={memTDificil}
                            onChange={setMemTDificil}
                            min={5}
                            max={180}
                            step={1}
                            ariaLabel="Tiempo para resolver difícil (segundos)"
                          />
                        </label>
                      </div>
                    </div>
                  </>
                )}
              </Section>
            </div>
          </div>
        )}

        {activeTab === "sonidos" && (
          // 3 columnas (pedido): Estilos / Sonidos por evento / Sonidos
          // antiguos lado a lado en pantallas anchas. items-start: cada card
          // mide su contenido sin estirarse a la vecina mas alta.
          <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
        {/* Migration 041: sonido del shopping. GLOBAL — uno solo para TODAS las
            sucursales (antes era por sucursal, en Mi pantalla). A diferencia de
            las cards de abajo (que guardan al instante), ésta se guarda con el
            botón "Guardar cambios" del pie, junto al resto del config global.
            Wrapper con col-span-3 (SettingCard no acepta className). */}
        <div className="lg:col-span-3">
          <SettingCard
            title={
              <span className="flex items-center gap-2">
                <Volume2 className="h-5 w-5 text-[#a78bfa]" />
                Sonido de las pantallas
              </span>
            }
          >
            <ToggleRow
              checked={soundEnabled}
              onChange={setSoundEnabled}
              label="Sonidos habilitados"
              desc="Si está OFF, los 3 juegos quedan en silencio en todas las pantallas (ni clicks ni fanfarrias ni efectos)."
            />

            {soundEnabled && (
              <div className="mt-4 rounded-lg bg-black/20 p-3">
                <div className="mb-3 flex items-center gap-1.5 text-sm font-medium opacity-80">
                  Estilo de sonido
                  <HelpPopover>
                    Los archivos de cada estilo se cargan en las cards de abajo
                    (Estilos de sonido / Sonidos por evento). Si no hay archivo
                    subido para un evento, suena un sonido sintético.
                  </HelpPopover>
                </div>
                {soundStyles.length === 0 ? (
                  <div className="text-xs opacity-60">
                    Cargando estilos disponibles…
                  </div>
                ) : (
                  <>
                    {soundStyleSlug &&
                      !soundStyles.some((s) => s.slug === soundStyleSlug) && (
                        <div className="mb-3 rounded-md border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs text-amber-200">
                          El estilo actual{" "}
                          <span className="font-mono">{soundStyleSlug}</span> está
                          inactivo. Elegí otro de la lista para seguir adelante.
                        </div>
                      )}
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {soundStyles.map((s) => (
                        <SoundStyleCard
                          key={s.id}
                          style={s}
                          selected={soundStyleSlug === s.slug}
                          onSelect={() => {
                            setSoundStyleSlug(s.slug);
                            setPageDirty(true);
                          }}
                          onPreview={() => playEvent("ruleta", "win", s.slug)}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
          </SettingCard>
        </div>

        {/* Aviso de modelo de guardado: a diferencia de las pestañas de
            Pantalla (que esperan el botón "Guardar cambios" de abajo), todo lo
            de Sonidos —crear/activar estilos y subir/borrar archivos— se
            guarda al instante. Sin este aviso el super_admin no sabe si
            tiene que apretar Guardar para confirmar un sonido. */}
        <div className="rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-xs text-emerald-100 lg:col-span-3">
          Los cambios de esta pestaña (estilos y archivos de sonido) se
          guardan <strong>al instante</strong>. No hace falta apretar
          "Guardar cambios".
        </div>
        <SettingCard title="Estilos de sonido">
          <SoundStylesCRUD />
        </SettingCard>

        <SettingCard title="Sonidos por evento">
          <SoundsByStyleAndEvent sounds={sounds} onChanged={refetchSounds} />
        </SettingCard>

        <SettingCard
          title="Sonidos antiguos"
          help={
            "Slots globales del modelo anterior (antes de los sonidos por estilo y evento). Se siguen reproduciendo como respaldo si no hay archivo cargado en el slot específico del estilo. Podés borrarlos cuando ya no los necesites."
          }
        >
          <div className="flex flex-col gap-3">
            {(Object.keys(SOUND_SLOTS) as SoundSlot[]).map((slot) => {
              const current = sounds.find((s) => s.slot_key === slot);
              return (
                <SoundUploader
                  key={slot}
                  slot={slot}
                  currentUrl={current?.url ?? null}
                  onChanged={refetchSounds}
                />
              );
            })}
          </div>
        </SettingCard>
          </div>
        )}

        {error && (
          <div className="rounded-lg bg-red-500/15 p-4 text-sm text-red-300">
            {error}
          </div>
        )}

        {/* Reorg UX 2026-06: barra de guardado sticky al pie. En una
            pagina larga el boton "Guardar cambios" quedaba enterrado;
            ahora queda siempre a mano mientras scrolleás. El feedback de
            exito es un toast (no un banner perdido al fondo). */}
        <div className="sticky bottom-0 z-10 -mx-1 mt-2 rounded-t-xl bg-[#0f0c29]/85 py-2 backdrop-blur">
          <button
            type="submit"
            disabled={saving}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#a78bfa] to-[#7c3aed] py-2.5 font-bold disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Save className="h-5 w-5" />
            )}
            Guardar cambios
          </button>
        </div>
      </form>
      </div>
    </div>
  );
}
